import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  AUTONOMY_INFO, AUTONOMY_LEVELS, autopilotEndDay, cloneLoopConfig, DEFAULT_LOOP_CONFIG, FIELD_LABELS, formatTokens,
  hourLabel, LIMIT_PRESETS, limitChoice, LOOP_LIMITS, LoopConfig, pipelineSteps, REPEAT_OPTIONS, scheduleText,
  TOUCH_FIELDS, toggleTouch, validateLoopConfig
} from './loop.logic';

/**
 * The loop settings form: on/off, how much freedom, what it may use and change,
 * limits per run, and when it runs. It edits the config it is given and emits the
 * updated copy on every change; the parent owns saving.
 */
@Component({
  selector: 'hour4u-admin-loop-config',
  templateUrl: './loop-config.component.html',
  styleUrls: ['./loop-config.component.scss']
})
export class LoopConfigComponent {
  @Input() config: LoopConfig = cloneLoopConfig(DEFAULT_LOOP_CONFIG);
  /** Used for the campaign end date shown with the schedule. */
  @Input() projectDetails: any;
  @Output() configChange = new EventEmitter<LoopConfig>();

  readonly autonomyLevels = AUTONOMY_LEVELS;
  readonly autonomyInfo = AUTONOMY_INFO;
  readonly touchFields = TOUCH_FIELDS;
  readonly fieldLabels = FIELD_LABELS;
  readonly limits = LOOP_LIMITS;
  readonly presets = LIMIT_PRESETS;
  readonly limitKeys = Object.keys(LIMIT_PRESETS);
  readonly repeatOptions = REPEAT_OPTIONS;
  readonly hours = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
  readonly hourLabel = hourLabel;
  readonly formatTokens = formatTokens;

  /** Limits where the owner picked "Type my own number", so the box stays open while typing. */
  private typing: { [key: string]: boolean } = {};

  get steps() {
    return pipelineSteps(this.config);
  }

  get modeLabel(): string {
    return AUTONOMY_INFO[this.config.autonomy].label;
  }

  get errors(): string[] {
    return validateLoopConfig(this.config);
  }

  get scheduleSentence(): string {
    const ist = new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
    return scheduleText(this.config, this.projectDetails, ist);
  }

  get campaignEnd(): string | null {
    return autopilotEndDay(DEFAULT_LOOP_CONFIG, this.projectDetails);
  }

  update(patch: Partial<LoopConfig>) {
    this.configChange.emit(Object.assign(cloneLoopConfig(this.config), patch));
  }

  // Limits
  choice(key: string): string {
    return this.typing[key] ? 'custom' : limitChoice(key, this.valueOf(key));
  }

  valueOf(key: string): number {
    return (this.config as any)[key];
  }

  pickLimit(key: string, choice: string) {
    if (choice === 'custom') {
      this.typing[key] = true;
      return;
    }
    this.typing[key] = false;
    this.update({ [key]: Number(choice) });
  }

  typeLimit(key: string, raw: string) {
    const digits = String(raw).replace(/[^0-9]/g, '');
    this.update({ [key]: digits === '' ? NaN : Number(digits) });
  }

  limitError(key: string): string {
    const limit = LOOP_LIMITS[key];
    const value = this.valueOf(key);
    if (typeof value !== 'number' || !isFinite(value) || Math.floor(value) !== value) {
      return 'Enter a whole number';
    }
    if (value < limit.min || value > limit.max) {
      return 'Between ' + formatTokens(limit.min) + ' and ' + formatTokens(limit.max);
    }
    return '';
  }

  // What it may change
  isTouchOn(field: string): boolean {
    return this.config.allowed_touch.indexOf(field) !== -1;
  }

  setTouch(field: string, on: boolean) {
    this.configChange.emit(toggleTouch(this.config, field, on));
  }

  trackByIndex(i: number) { return i; }
}
